import { useState, useContext, useCallback } from "react";
import api from "../utils/axios";
import { AuthContext } from "../context/AuthContext";
import { useToast } from "@chakra-ui/react";

const useTickets = () => {
    const { currLocation } = useContext(AuthContext);
    const [tickets, setTickets] = useState([]);
    const [recurringTickets, setRecurringTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const toast = useToast();

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/tickets/location/${currLocation}`);
            setTickets(response.data);
            setError(null);
        } catch (err) {
            setError("Failed to load tickets.");
        } finally {
            setLoading(false);
        }
    };

    const fetchRecurringTickets = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/recurring-tickets/location/${currLocation}`);
            setRecurringTickets(response.data);
            setError(null);
        } catch (err) {
            setError("Failed to load recurring tickets.");
        } finally {
            setLoading(false);
        }
    };
    
    const getShareableLink = useCallback(async (ticketId) => {
        try {
            const response = await api.post(`/tickettoken/${ticketId}/generate-token`);
            return response.data.link;
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to generate share link.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return null;
        }
    }, [toast]);
    
    const createTicket = async (newTicket, onClose) => {
        try {
            if (typeof newTicket !== "object" || newTicket instanceof Event) {
                throw new Error("Invalid data: newTicket contains a circular reference or is an event object");
            }

            const formData = new FormData();
            formData.append("name", newTicket.name);
            formData.append("description", newTicket.description);
            formData.append("sectionId", newTicket.sectionId);
            if (newTicket.image) {
                formData.append("image", newTicket.image);
            }

            await api.post(`/tickets/offline/location/${currLocation}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            await fetchTickets();

            toast({
                title: "Ticket Created",
                description: "The ticket has been created successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            onClose();
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to create ticket.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const createRecurringTicket = async (newTicket, onClose) => {
        try {
            if (typeof newTicket !== "object" || newTicket instanceof Event) {
                throw new Error("Invalid data: newTicket contains a circular reference or is an event object");
            }

            const formData = new FormData();
            formData.append("name", newTicket.name);
            formData.append("description", newTicket.description);
            formData.append("sectionId", newTicket.sectionId);
            formData.append("recurrence", newTicket.recurrence);
            if (newTicket.image) {
                formData.append("image", newTicket.image);
            }

            await api.post(`/recurring-tickets/offline/location/${currLocation}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            await fetchRecurringTickets(); 

            toast({
                title: "Ticket Created",
                description: "The ticket has been created successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            onClose();
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to create ticket.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const updateTicketStatus = async (ticketId, newStatus) => {
        try {
            await api.put(`/tickets/${ticketId}/status`, null, {
                params: { locationId: currLocation, status: newStatus },
            });

            setTickets((prevTickets) =>
                prevTickets.map((ticket) =>
                    ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
                )
            );

            toast({
                title: "Status Updated",
                description: "The ticket status has been changed successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to update ticket status.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const updateRecurringTicketStatus = async (ticketId, newStatus) => {
        try {
            await api.put(`/recurring-tickets/${ticketId}/status`, null, {
                params: { locationId: currLocation, status: newStatus },
            });

            setRecurringTickets((prevTickets) =>
                prevTickets.map((ticket) =>
                    ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
                )
            );

            toast({
                title: "Status Updated",
                description: "The ticket status has been changed successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to update ticket status.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const confirmCompletion = async (ticketId) => {
        try {
            var modifiedTicket = await api.put(`/recurring-tickets/${ticketId}/complete`, null, {
                params: { locationId: currLocation, ticketId: ticketId },
            });

            setRecurringTickets((prevTickets) =>
                prevTickets.map((_ticket) =>
                    _ticket.id === ticketId ? { 
                        ..._ticket, 
                        lastCompletedByName: modifiedTicket.data.lastCompletedByName,
                        lastCompletedAt: modifiedTicket.data.lastCompletedAt
                    } : _ticket
                )
            );

            toast({
                title: "Zadatak je odrađen",
                description: "Zadatak je uspješno označen kao odrađen.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to update ticket status.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const updateRecurrenceStatus = async (ticketId, newReccurence) => {
        try {
            await api.put(`/recurring-tickets/${ticketId}/recurrence`, null, {
                params: { locationId: currLocation, recurrence: newReccurence },
            });

            setRecurringTickets((prevTickets) =>
                prevTickets.map((ticket) =>
                    ticket.id === ticketId ? { ...ticket, recurrence: newReccurence } : ticket
                )
            );

            toast({
                title: "Reccurence Updated",
                description: "The ticket recurrence has been changed successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to update ticket status.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };


    const addComment = async (ticketId, commentText) => {
        try {
            const response = await api.post(`/recurring-tickets/${ticketId}/comment`, null, {
                params: { commentText }
              });

            toast({
                title: "Comment is added",
                description: "The comment has been successfully added.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            
            return response.data;
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to update ticket status.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };
    
    return { tickets, recurringTickets, addComment, updateRecurrenceStatus, fetchTickets, fetchRecurringTickets, loading, error, createTicket, confirmCompletion, createRecurringTicket, updateTicketStatus, updateRecurringTicketStatus, getShareableLink };
};

export default useTickets;
